import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatMenu } from '@angular/material/menu';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { getCurrencySymbol } from '@angular/common';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import TimeUtils from 'app/shared/utils/time-utils';
import { FORMATS_DASH_NUMBER } from 'app/shared/utils/format-data-picker';
import { MondayFirstDateAdapter } from 'app/shared/utils/monday-first-date-adapter';
import { DataPlatformAudienceRule, DIMENSION_TYPES } from '../../../data-platform/data-platform.model';
import { getDimensionTypeLabel } from '../../../data-platform/data-platform.utils';
import {
  COUNT_OPERATOR_OPTIONS,
  DAYS_AGO_OPERATOR_OPTIONS,
  OPERATOR_OPTIONS,
  OperatorOption,
  REVENUE_OPERATOR_OPTIONS,
  RULE_TYPE_ICON_MAP,
  RULE_TYPE_LABEL_MAP,
  TIMEFRAME_MODE_OPTIONS,
  deriveTimeframeMode,
} from '../../../data-platform/audiences/rule-types.config';
import {
  RuleNumberProblem,
  dayRangeProblem,
  ruleNumberProblem,
  rulesToAudienceFilters,
} from '../../../data-platform/audiences/rule-to-audience-filter.utils';
import { ruleKey } from '../../../data-platform/audiences/audience-filter-to-dp-rule.utils';
import { shiftDays, startOfDay } from '../../../analytics/shared/analysis-dates';
import {
  CUSTOM_STRENGTH_DESC_KEY,
  CUSTOM_STRENGTH_LABEL_KEY,
  DEFAULT_DIMENSION_STRENGTH,
  DIMENSION_STRENGTHS,
  DimensionHint,
  DimensionStrength,
  dimensionHint,
  findStrengthPreset,
  rulesToStrength,
  strengthShare,
  strengthToRules,
} from '../../../data-platform/audiences/dimension-presets.utils';

/**
 * Inline editor for a single DataPlatformAudienceRule. The flyout's Build tab
 * renders one of these per rule. Rule changes are emitted up via ruleChange;
 * the parent owns the rules array. A `remove` event is emitted when the user
 * clicks the delete button — the parent decides whether to honor it.
 *
 * Each rule type carries different fields (operator + value vs daysAgo vs
 * widget id / event type / etc), so the template switches over rule.type and
 * renders the relevant inputs. The form uses every rule field as a control —
 * inactive fields are still in the form but ignored when we project back to
 * a DataPlatformAudienceRule on emit, so we don't ship phantom fields.
 */
@Component({
  selector: 'app-audience-rule-editor',
  templateUrl: './audience-rule-editor.component.html',
  styleUrls: ['./audience-rule-editor.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'sv_SE' },
    { provide: DateAdapter, useClass: MondayFirstDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: FORMATS_DASH_NUMBER },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AudienceRuleEditorComponent implements OnInit, OnChanges {
  /** The rule being edited. The component takes a deep copy on init/changes. */
  @Input({ required: true }) rule!: DataPlatformAudienceRule;

  /** The `dimensions` floor paired with a `dimension_affinity` rule, when the
   *  criterion resolved to a pair. Both are edited as one row. */
  @Input() floorRule?: DataPlatformAudienceRule;

  /** Autocomplete options — loaded once by the parent and passed in. */
  @Input() eventTypes: readonly string[] = [];
  @Input() goalNames: readonly string[] = [];
  @Input() tags: readonly string[] = [];
  @Input() dimensionValuesByType: Readonly<Record<string, readonly string[]>> = {};

  /** Content dimensions switched on for this account (`account.dimensions.settings`). */
  @Input() accountDimensions: readonly string[] = [];
  @Input() widgetList: ReadonlyArray<{ id: number; name?: string; campaignName?: string }> = [];
  @Input() widgetEventTypes: readonly string[] = [];
  @Input() consentVersionOptions: readonly string[] = [];
  @Input() optionsLoading = false;

  /** Currency code (e.g. 'USD') for the revenue rule's currency-symbol suffix. */
  @Input() currency: string | null = null;

  /** Disables the rule type selector — used for default audiences we can't reshape. */
  @Input() readOnly = false;

  /** One rule, or a Content interest pair as affinity followed by floor. */
  @Output() ruleChange = new EventEmitter<DataPlatformAudienceRule[]>();
  @Output() remove = new EventEmitter<void>();

  /** The shared "Add criterion" picker (owned by the flyout). The type button
   *  opens it and `changeTypeRequested` fires first so the parent targets this
   *  row for in-place replacement. */
  @Input() sharedTypeMenu?: MatMenu;
  @Output() changeTypeRequested = new EventEmitter<void>();
  /** Fires when the shared picker opens from this row's type chip, so the parent
   *  can move focus into the search box. */
  @Output() typeMenuOpened = new EventEmitter<void>();

  // ── Static template inputs ────────────────────────────────────────────────
  protected readonly ruleTypeIconMap = RULE_TYPE_ICON_MAP;
  protected readonly ruleTypeLabelMap = RULE_TYPE_LABEL_MAP;
  protected readonly timeframeModeOptions = TIMEFRAME_MODE_OPTIONS;
  protected readonly strengthPresets = DIMENSION_STRENGTHS;
  protected readonly customStrengthLabelKey = CUSTOM_STRENGTH_LABEL_KEY;
  protected readonly customStrengthDescKey = CUSTOM_STRENGTH_DESC_KEY;
  protected readonly listSeparatorKeys = [ENTER, COMMA] as const;

  /** Outside the form, so typing a chip does not emit a rule. */
  protected readonly listInput = new FormControl<string>('', { nonNullable: true });

  private readonly listTrigger = viewChild<MatAutocompleteTrigger>('listTrigger');

  /** Maps a widget event type to a translation key. Unknown types fall back to
   *  the raw value in the template (translate echoes a non-key unchanged). */
  protected readonly widgetEventLabelKeys: Readonly<Record<string, string>> = {
    'widget-open': 'visits.criteriaProperties.widget-opens',
    'widget-conversion': 'visits.criteriaProperties.widget-conversions',
    'widget-dismissal': 'visits.criteriaProperties.widget-dissmissals',
    'widget-clickthrough': 'visits.criteriaProperties.widget-clickthroughs',
    'widget-survey-response': 'visits.criteriaProperties.widget-survey-response',
  };

  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly dimensionTypeLabels: Readonly<Record<string, string>> = Object.fromEntries(
    DIMENSION_TYPES.map((type) => [type, getDimensionTypeLabel(type, this.translate)]),
  );

  private storedGoals: readonly string[] = [];

  /**
   * The form keeps every rule field as a control so the template can bind
   * uniformly via formControlName. We only emit fields that are meaningful
   * for the current rule type in `toRule()`.
   */
  protected form: FormGroup = this.buildForm(undefined);

  /**
   * Held when the wire rules cannot express the choice: a criterion with no
   * value yet emits an inert rule, and Custom numbers matching a preset's would
   * read back as that preset. Null once the rules can carry it again.
   */
  private pending: { strength: DimensionStrength; share: number | null; floor: number | null } | null = null;

  private lastEmitted: DataPlatformAudienceRule[] = [];

  /** Mirrors filtersForSave: an unchanged rule keeps its stored filter, so it skips the range check. */
  private loadedRuleKey = '';

  ngOnInit(): void {
    this.loadedRuleKey = ruleKey(this.rule);
    this.form = this.buildForm(this.rule);
    this.subscribeToFormChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const changed = changes['rule'] ?? changes['floorRule'];
    if (changed && !changed.firstChange && !this.isOwnEcho()) {
      this.form = this.buildForm(this.rule);
      this.subscribeToFormChanges();
    }
  }

  protected onTimeframeModeChanged(mode: string): void {
    const today = startOfDay(new Date());
    this.form.patchValue(
      {
        timeframeMode: mode,
        daysAgo: mode === 'TimeWindow' || mode === 'BetweenDaysAgo' ? 30 : null,
        daysAgoUpper: mode === 'BetweenDaysAgo' ? 90 : null,
        startDate: mode === 'SinceDate' || mode === 'BetweenDates' ? shiftDays(today, -30) : null,
        endDate: mode === 'BetweenDates' ? today : null,
      },
      { emitEvent: true },
    );
  }

  protected onDaysAgoModeChanged(mode: string): void {
    const daysAgo = (this.form.get('daysAgo')?.value as number | null) ?? 0;
    const daysAgoUpper = mode === 'between' ? Math.max(DEFAULT_DAYS_AGO_UPPER, Math.floor(daysAgo) + 1) : null;
    this.form.patchValue({ operator: mode, daysAgoUpper });
  }

  /** Leaving "not at all" resets a count of 0 to 1. */
  protected onOperatorSelected(operator: string): void {
    const value = this.form.get('value')?.value as number | null;
    if (this.isCountRule(this.currentType) && operator !== NO_COUNT_OPERATOR && (value == null || value < 1)) {
      this.form.patchValue({ value: 1 });
    }
  }

  /** A stored version no longer offered stays selectable. */
  protected get consentOptions(): readonly string[] {
    const current = this.form.get('versionDate')?.value as string | null;
    return current && !this.consentVersionOptions.includes(current)
      ? [...this.consentVersionOptions, current]
      : this.consentVersionOptions;
  }

  protected isCountRule(type: string): boolean {
    return type === 'goal_count' || type === 'purchase_count';
  }

  protected isTagRule(type: string): boolean {
    return type === 'has_tag' || type === 'has_not_tag';
  }

  /** A loaded operator outside the choices stays visible. */
  protected get rowOperatorOptions(): readonly OperatorOption[] {
    const base = this.operatorOptionsFor(this.currentType);
    const current = this.form.get('operator')?.value as string | null;
    const loaded =
      current && !base.some((op) => op.value === current)
        ? OPERATOR_OPTIONS.find((op) => op.value === current)
        : undefined;
    return loaded ? [...base, loaded] : base;
  }

  /** A rule saved without an operator is read as `lte`. */
  protected get daysAgoMode(): string {
    return (this.form.get('operator')?.value as string | null) ?? 'lte';
  }

  protected get showsValue(): boolean {
    return this.form.get('operator')?.value !== NO_COUNT_OPERATOR;
  }

  protected get blockingNoteKey(): string | null {
    const type = this.currentType;
    if ((type === 'goal_count' || this.isTagRule(type)) && this.currentList.length === 0) {
      return 'data-platform.rule-needs-values';
    }
    if (this.isDimensionRule(type)) {
      return null;
    }
    const rule = this.toRule();
    const changed = ruleKey(rule) !== this.loadedRuleKey;
    const problem = ruleNumberProblem(rule) ?? (changed ? dayRangeProblem(rule) : null);
    if (problem) {
      return NUMBER_PROBLEM_NOTE_KEYS[problem];
    }
    return rulesToAudienceFilters([rule]).unsavable.length ? 'data-platform.rule-unsavable-note' : null;
  }

  protected get currentList(): readonly string[] {
    return (this.form.get(this.listControlName)?.value as string[] | null) ?? [];
  }

  /** Stored goals missing from goalNames come first, so they stay selectable. */
  protected get goalOptions(): ReadonlyArray<{ value: string; label: string }> {
    const missing = this.storedGoals.filter((goal) => !this.goalNames.includes(goal));
    return [...missing, ...this.goalNames].map((value) => ({ value, label: this.goalLabel(value) }));
  }

  protected get tagPlaceholderKey(): string {
    if (this.currentList.length) {
      return '';
    }
    return this.optionsLoading ? 'data-platform.loading-options' : 'data-platform.list-input-hint';
  }

  protected get listValueOptions(): readonly string[] {
    const chosen = new Set(this.currentList);
    const typed = this.listInput.value.trim().toLowerCase();
    return this.tags
      .filter((value) => !chosen.has(value) && (!typed || value.toLowerCase().includes(typed)))
      .slice(0, MAX_VALUE_OPTIONS);
  }

  protected addListValue(event: MatChipInputEvent): void {
    // Enter on a highlighted suggestion adds the suggestion, not the typed text.
    const trigger = this.listTrigger();
    if (trigger?.panelOpen && trigger.activeOption) {
      return;
    }
    this.appendListValue(event.value);
    event.chipInput.clear();
  }

  protected selectListValue(event: MatAutocompleteSelectedEvent): void {
    this.appendListValue(String(event.option.value));
  }

  protected removeListValue(value: string): void {
    this.form.get(this.listControlName)?.setValue(this.currentList.filter((item) => item !== value));
  }

  protected get currencySymbol(): string {
    return this.currency ? getCurrencySymbol(this.currency.toUpperCase(), 'narrow') : '';
  }

  protected onRemove(): void {
    this.remove.emit();
  }

  /** When the parent supplies the shared picker, ask it to target this row for
   *  replacement before the menu opens (the click also triggers the menu). */
  protected onTypeBtnClick(): void {
    if (this.sharedTypeMenu) this.changeTypeRequested.emit();
  }

  /** Convenience for template — used by *ngIf style conditional rendering. */
  protected get currentType(): string {
    return (this.form.get('type')?.value as string) ?? '';
  }

  protected isOperatorRule(type: string): boolean {
    return isOperatorRule(type);
  }

  protected isDimensionRule(type: string): boolean {
    return type === 'dimensions' || type === 'dimension_affinity';
  }

  /**
   * The account's allow-list is what the tracking script extracts, so a type
   * outside it is never collected. Empty means "all" to the tracker, hence the
   * fallbacks.
   */
  protected get availableDimensionTypes(): readonly string[] {
    const configured = DIMENSION_TYPES.filter((t) => this.accountDimensions.includes(t));
    if (configured.length) {
      return configured;
    }
    const withData = DIMENSION_TYPES.filter((t) => (this.dimensionValuesByType[t]?.length ?? 0) > 0);
    return withData.length ? withData : DIMENSION_TYPES;
  }

  protected get noDimensionsConfigured(): boolean {
    return !this.accountDimensions.length && !Object.keys(this.dimensionValuesByType).length;
  }

  /** mat-autocomplete does no filtering of its own. Source is usage-ordered,
   *  so the cap keeps the most common values. */
  protected get dimensionValueOptions(): readonly string[] {
    const raw = this.form.getRawValue();
    const all = this.dimensionValuesByType[raw.dimensionType ?? ''] ?? [];
    const typed = (raw.dimensionValue ?? '').trim().toLowerCase();
    const matches = typed ? all.filter((v) => v.toLowerCase().includes(typed)) : all;
    return matches.slice(0, MAX_VALUE_OPTIONS);
  }

  protected get dimensionValuePlaceholderKey(): string {
    if (this.optionsLoading) {
      return 'data-platform.loading-options';
    }
    const type = this.form.getRawValue().dimensionType;
    const hasValues = (this.dimensionValuesByType[type ?? '']?.length ?? 0) > 0;
    return type && !hasValues ? 'data-platform.dimension-no-values' : '';
  }

  protected get selectedStrengthLabelKey(): string {
    const strength = this.form.get('strength')?.value as DimensionStrength;
    return findStrengthPreset(strength)?.labelKey ?? CUSTOM_STRENGTH_LABEL_KEY;
  }

  protected get resolvedStrength(): { share: number | null; floor: number | null; operator: 'gte' | 'lt' } {
    const raw = this.form.getRawValue();
    const strength = (raw.strength ?? DEFAULT_DIMENSION_STRENGTH) as DimensionStrength;
    if (strength === 'custom') {
      return { share: raw.share ?? null, floor: raw.floor ?? null, operator: 'gte' };
    }
    const preset = findStrengthPreset(strength);
    return {
      share: preset ? strengthShare(preset, this.compositeDimension()) : null,
      floor: preset?.floorValue ?? null,
      operator: preset?.floorOperator ?? 'gte',
    };
  }

  protected get dimensionHint(): DimensionHint | null {
    const raw = this.form.getRawValue();
    const type = raw.dimensionType ?? '';
    const resolved = this.resolvedStrength;
    return dimensionHint(
      this.dimensionTypeLabels[type] || type,
      raw.dimensionValue ?? '',
      resolved.share,
      resolved.floor,
      resolved.operator,
    );
  }

  protected get dimensionWarningKey(): string | null {
    const raw = this.form.getRawValue();
    if (!raw.dimensionType || raw.dimensionValue) {
      return null;
    }
    return raw.strength === 'never'
      ? 'data-platform.strength-needs-value-negative'
      : 'data-platform.strength-needs-value';
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private operatorOptionsFor(type: string): readonly OperatorOption[] {
    if (this.isCountRule(type)) {
      return COUNT_OPERATOR_OPTIONS;
    }
    if (type === 'revenue') {
      return REVENUE_OPERATOR_OPTIONS;
    }
    if (type === 'last_active' || type === 'first_seen') {
      return DAYS_AGO_OPERATOR_OPTIONS;
    }
    return OPERATOR_OPTIONS.filter((op) => op.value !== 'between' || type === 'engagement_score');
  }

  private get listControlName(): 'names' | 'tags' {
    return this.isTagRule(this.currentType) ? 'tags' : 'names';
  }

  private appendListValue(raw: string): void {
    const value = raw.trim();
    this.listInput.setValue('');
    if (!value || this.currentList.includes(value)) {
      return;
    }
    this.form.get(this.listControlName)?.setValue([...this.currentList, value]);
  }

  private goalLabel(value: string): string {
    const match = ONSITE_CAMPAIGN_GOAL.exec(value);
    if (!match) {
      return value;
    }
    const widget = this.widgetList.find((candidate) => candidate.id === Number(match[2]));
    const campaign = widget?.campaignName || widget?.name || match[2];
    return this.translate.instant(`data-platform.onsite-goal-${match[1]}`, { campaign });
  }

  private buildForm(rule: DataPlatformAudienceRule | undefined): FormGroup {
    this.storedGoals =
      rule?.type === 'goal_count' ? [...new Set([...this.storedGoals, ...listOf(rule.values, rule.eventName)])] : [];
    // Dimension rules store "type:value" combined; split for editing.
    const composite = rule?.dimension ?? '';
    const sep = composite.indexOf(':');
    const dimensionType = sep > 0 ? composite.slice(0, sep) : composite;
    const dimensionValue = sep > 0 ? composite.slice(sep + 1) : '';

    const affinity = rule?.type === 'dimension_affinity' ? rule : undefined;
    const floor = rule?.type === 'dimensions' ? rule : this.floorRule;
    const pending = rule && this.isDimensionRule(rule.type) ? this.pending : null;
    const strength =
      rule && this.isDimensionRule(rule.type)
        ? (pending?.strength ??
          (composite.includes(':') ? rulesToStrength(affinity, floor) : DEFAULT_DIMENSION_STRENGTH))
        : null;

    const countsNone = !!rule && this.isCountRule(rule.type) && rule.operator === 'eq' && rule.value === 0;

    return this.fb.group({
      type: [{ value: rule?.type ?? 'event_count', disabled: this.readOnly }],
      eventName: [rule?.eventName ?? ''],
      names: [rule?.type === 'goal_count' ? listOf(rule.values, rule.eventName) : []],
      tags: [rule && this.isTagRule(rule.type) ? listOf(rule.values, rule.tag) : []],
      tagMode: [rule?.type === 'has_not_tag' ? 'none' : 'any'],
      operator: [countsNone ? NO_COUNT_OPERATOR : (rule?.operator ?? null)],
      value: [rule?.value ?? null],
      upperValue: [rule?.upperValue ?? null],
      couponOnly: [rule?.couponOnly ?? false],
      daysAgo: [rule?.daysAgo ?? null],
      daysAgoUpper: [rule?.daysAgoUpper ?? null],
      startDate: [rule?.startDate ? TimeUtils.getMomentFormatted(rule.startDate.slice(0, 10), 'YYYY-MM-DD') : null],
      endDate: [rule?.endDate ? TimeUtils.getMomentFormatted(rule.endDate.slice(0, 10), 'YYYY-MM-DD') : null],
      timeframeMode: [deriveTimeframeMode(rule ?? {})],
      tag: [rule?.tag ?? ''],
      dimensionType: [dimensionType],
      dimensionValue: [dimensionValue],
      strength: [strength ?? DEFAULT_DIMENSION_STRENGTH],
      share: [pending ? pending.share : (affinity?.value ?? null)],
      floor: [pending ? pending.floor : (floor?.value ?? null)],
      widgetId: [rule?.widgetId ?? null],
      widgetEventType: [rule?.widgetEventType ?? ''],
      versionDate: [rule?.versionDate ?? ''],
    });
  }

  private subscribeToFormChanges(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncResolvedStrengthValues();
      this.lastEmitted = this.toRules();
      this.ruleChange.emit(this.lastEmitted);
    });
  }

  /** Rebuilding from our own echo would wipe what the rules can't carry, such as a half-typed number. */
  private isOwnEcho(): boolean {
    return this.rule === this.lastEmitted[0] && this.floorRule === this.lastEmitted[1];
  }

  private compositeDimension(): string {
    const raw = this.form.getRawValue();
    return raw.dimensionType && raw.dimensionValue
      ? `${raw.dimensionType}:${raw.dimensionValue}`
      : (raw.dimensionType ?? '');
  }

  /**
   * Keeps the hidden share / floor controls on the active preset's resolved
   * values, so Custom opens on what was already on screen.
   */
  private syncResolvedStrengthValues(): void {
    const raw = this.form.getRawValue();
    if (!this.isDimensionRule(raw.type) || raw.strength === 'custom') {
      return;
    }
    const preset = findStrengthPreset(raw.strength as DimensionStrength);
    this.form.patchValue(
      {
        share: preset ? strengthShare(preset, this.compositeDimension()) : null,
        floor: preset?.floorValue ?? null,
      },
      { emitEvent: false },
    );
  }

  /**
   * Projects the form back to clean wire rules, dropping fields that don't
   * apply to the current type so we don't ship phantom data to the backend.
   */
  private toRules(): DataPlatformAudienceRule[] {
    const raw = this.form.getRawValue();
    if (this.isDimensionRule(raw.type)) {
      const dimension = this.compositeDimension();
      const strength = (raw.strength ?? DEFAULT_DIMENSION_STRENGTH) as DimensionStrength;
      const wireCanExpressIt = dimension.includes(':') && strength !== 'custom';
      this.pending = wireCanExpressIt ? null : { strength, share: raw.share ?? null, floor: raw.floor ?? null };
      return strengthToRules(strength, dimension, { share: raw.share, floor: raw.floor });
    }
    return [this.toRule()];
  }

  private toRule(): DataPlatformAudienceRule {
    const raw = this.form.getRawValue();
    const out: DataPlatformAudienceRule = { type: raw.type };

    switch (raw.type) {
      case 'is_identified':
      case 'is_anonymous':
      case 'is_logged_in':
      case 'is_logged_out':
        break;

      case 'has_consent':
      case 'has_not_consent':
        if (raw.versionDate) out.versionDate = raw.versionDate;
        break;

      case 'event_count':
        if (raw.eventName) out.eventName = raw.eventName;
        if (raw.operator) out.operator = raw.operator;
        if (raw.value != null) out.value = raw.value;
        this.encodeTimeframe(raw, out);
        break;

      case 'goal_count': {
        const names = (raw.names ?? []) as string[];
        if (names.length === 1) {
          out.eventName = names[0];
        } else if (names.length > 1) {
          out.values = [...names];
        }
        this.encodeCount(raw, out);
        this.encodeTimeframe(raw, out);
        break;
      }

      case 'widget_event_count':
        if (raw.widgetId != null) out.widgetId = raw.widgetId;
        if (raw.widgetEventType) out.widgetEventType = raw.widgetEventType;
        if (raw.operator) out.operator = raw.operator;
        if (raw.value != null) out.value = raw.value;
        this.encodeTimeframe(raw, out);
        break;

      case 'total_events':
      case 'session_count':
        if (raw.operator) out.operator = raw.operator;
        if (raw.value != null) out.value = raw.value;
        this.encodeTimeframe(raw, out);
        break;

      case 'purchase_count':
        this.encodeCount(raw, out);
        if (this.rule?.type === 'purchase_count' && this.rule.couponOnly) {
          out.couponOnly = true;
        }
        this.encodeTimeframe(raw, out);
        break;

      case 'last_active':
      case 'first_seen':
        if (raw.daysAgo != null) out.daysAgo = raw.daysAgo;
        if (raw.operator) out.operator = raw.operator;
        if (raw.operator === 'between' && raw.daysAgoUpper != null) {
          out.daysAgoUpper = raw.daysAgoUpper;
        }
        break;

      case 'revenue':
        if (raw.operator) out.operator = raw.operator;
        if (raw.value != null) out.value = raw.value;
        if (raw.operator === 'between' && raw.upperValue != null) out.upperValue = raw.upperValue;
        if (raw.couponOnly) out.couponOnly = true;
        this.encodeTimeframe(raw, out);
        break;

      case 'has_tag':
      case 'has_not_tag': {
        out.type = raw.tagMode === 'none' ? 'has_not_tag' : 'has_tag';
        const tags = (raw.tags ?? []) as string[];
        if (tags.length === 1) {
          out.tag = tags[0];
        } else if (tags.length > 1) {
          out.values = [...tags];
        }
        break;
      }

      case 'engagement_score':
        if (raw.operator) out.operator = raw.operator;
        if (raw.value != null) out.value = raw.value;
        if (raw.upperValue != null) out.upperValue = raw.upperValue;
        break;
    }

    return out;
  }

  private encodeCount(raw: Record<string, unknown>, out: DataPlatformAudienceRule): void {
    if (raw['operator'] === NO_COUNT_OPERATOR) {
      out.operator = 'eq';
      out.value = 0;
      return;
    }
    if (raw['operator']) {
      out.operator = raw['operator'] as string;
    }
    if (raw['value'] != null) {
      out.value = raw['value'] as number;
    }
  }

  /**
   * Writes the appropriate timeframe fields onto `out` based on the
   * `timeframeMode` the user picked. The mode itself is UI-only and doesn't
   * persist on the rule — the presence/absence of date / daysAgo fields
   * encodes it. `deriveTimeframeMode` recovers it on load.
   */
  private encodeTimeframe(raw: Record<string, unknown>, out: DataPlatformAudienceRule): void {
    const mode = raw['timeframeMode'] as string;
    switch (mode) {
      case 'TimeWindow':
        if (raw['daysAgo'] != null) out.daysAgo = raw['daysAgo'] as number;
        break;
      case 'SinceDate':
        if (raw['startDate']) out.startDate = this.toUtcDay(raw['startDate']);
        break;
      case 'BetweenDates':
        if (raw['startDate']) out.startDate = this.toUtcDay(raw['startDate']);
        if (raw['endDate']) out.endDate = this.toUtcEndDate(raw['endDate']);
        break;
      case 'BetweenDaysAgo':
        if (raw['daysAgo'] != null) out.daysAgo = raw['daysAgo'] as number;
        if (raw['daysAgoUpper'] != null) out.daysAgoUpper = raw['daysAgoUpper'] as number;
        break;
      default:
        // 'any time' — leave timeframe fields absent
        break;
    }
  }

  /** The picked day as the UTC midnight the server reads; toISOString() of local midnight is the previous day. */
  private toUtcDay(picked: unknown): string {
    return `${TimeUtils.getFormattedDate(picked)}T00:00:00.000Z`;
  }

  /** The server reads endDate as an inclusive instant, so a changed end day runs to its last UTC moment. */
  private toUtcEndDate(picked: unknown): string {
    const day = TimeUtils.getFormattedDate(picked);
    const stored = this.rule?.endDate;
    return stored?.slice(0, 10) === day ? stored : `${day}T23:59:59.999Z`;
  }
}

const MAX_VALUE_OPTIONS = 50;

/** Form-only; saved as a count of 0. */
const NO_COUNT_OPERATOR = 'none';

const NUMBER_PROBLEM_NOTE_KEYS: Readonly<Record<RuleNumberProblem, string>> = {
  missing: 'data-platform.rule-needs-number',
  score: 'data-platform.rule-needs-score',
  negative: 'data-platform.rule-needs-zero-or-more',
  dayRange: 'data-platform.rule-needs-day-range',
};

const DEFAULT_DAYS_AGO_UPPER = 90;

const ONSITE_CAMPAIGN_GOAL = /^oc_(click|submit|discount)_(\d+)$/;

function listOf(values: readonly string[] | undefined, single: string | undefined): string[] {
  return values?.length ? [...values] : single ? [single] : [];
}

/** Rule types that surface a numeric operator + value pair in the editor. */
function isOperatorRule(type: string): boolean {
  return (
    type === 'event_count' ||
    type === 'goal_count' ||
    type === 'revenue' ||
    type === 'total_events' ||
    type === 'session_count' ||
    type === 'purchase_count' ||
    type === 'engagement_score' ||
    type === 'widget_event_count'
  );
}
